import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ApplicationButton,
  ApplicationPage,
  ApplicationStateRegion,
  ApplicationSurface,
  ApplicationViewHeader,
} from "../../../components/application";
import {
  completeAssignmentAttempt,
  getAssignedAssignment,
  startOrResumeAssignment,
} from "../services/assignmentApi";
import {
  formatAssignmentDate,
  participantAssignmentErrorMessage,
  participantActionLabel,
  participantProgressLabel,
  participantStatusDetail,
  participantStatusMessage,
  targetLabel,
} from "./assignmentViewUtils";
import "./assignmentPresentation.css";

export default function AssignedWorkDetail() {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function loadAssignment() {
      try {
        setLoading(true);
        const loaded =
          await getAssignedAssignment(assignmentId);

        if (active) {
          setAssignment(loaded?.assignment || null);
          setProgress(loaded?.progress || null);
          setError("");
        }
      } catch (_loadError) {
        if (active) {
          setError(
            participantAssignmentErrorMessage(
              _loadError,
              "This assignment could not be loaded, or it is not assigned to you."
            )
          );
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadAssignment();

    return () => {
      active = false;
    };
  }, [assignmentId]);

  async function handleAction() {
    try {
      setStarting(true);

      if (progress?.status === "submitted") {
        const confirmed =
          window.confirm(
            "Finish this assignment? Your submitted responses will be marked complete. No further response changes are available after this step."
          );

        if (!confirmed) {
          return;
        }

        const completed =
          await completeAssignmentAttempt(assignmentId);

        setAssignment(completed?.assignment || assignment);
        setProgress(completed?.progress || progress);
        setError("");
        return;
      }

      await startOrResumeAssignment(assignmentId);
      navigate(`/app/assigned-work/${assignmentId}/run`);
    } catch (_startError) {
      setError(
        participantAssignmentErrorMessage(
          _startError,
          progress?.status === "submitted"
            ? "This assignment could not be finished right now."
            : "This assignment cannot be opened right now."
        )
      );
    } finally {
      setStarting(false);
    }
  }

  if (loading) {
    return (
      <ApplicationPage variant="workspace">
        <ApplicationViewHeader
          contextLabel="Assigned Work"
          title="Loading assignment..."
        />
      </ApplicationPage>
    );
  }

  const statusMessage =
    participantStatusMessage(progress);
  const statusDetail =
    participantStatusDetail(progress);

  const actionDisabled =
    starting ||
    !progress?.isAvailable ||
    progress?.status === "completed";

  return (
    <ApplicationPage variant="workspace">
      <ApplicationViewHeader
        contextLabel="Assigned Work"
        title={assignment?.title || "Assignment"}
        description={assignment?.description || ""}
      />

      <p>
        <Link to="/app/assigned-work">
          Back to Assigned Work
        </Link>
      </p>

      {error && (
        <ApplicationStateRegion
          status="error"
          message={error}
        />
      )}

      {assignment && (
        <ApplicationSurface>
          <dl className="av-assignment-metadata">
            <dt>Activity</dt>
            <dd>{targetLabel(assignment.target)}</dd>
            <dt>Status</dt>
            <dd>{participantProgressLabel(progress)}</dd>
            <dt>Available from</dt>
            <dd>{formatAssignmentDate(assignment.availableFrom)}</dd>
            <dt>Due</dt>
            <dd>{formatAssignmentDate(assignment.dueAt)}</dd>
            <dt>Closes</dt>
            <dd>{formatAssignmentDate(assignment.closesAt)}</dd>
          </dl>

          {assignment.instructions && (
            <div className="av-assignment-detail__instructions">
              <h2>Assignment instructions</h2>
              <p>{assignment.instructions}</p>
            </div>
          )}

          {statusMessage && (
            <ApplicationStateRegion
              status={
                progress?.status === "completed"
                  ? "success"
                  : progress?.isClosed
                    ? "warning"
                    : "information"
              }
              message={statusMessage}
              detail={statusDetail}
            />
          )}

          {progress?.status !== "completed" ? (
            <ApplicationButton disabled={actionDisabled} onClick={handleAction}>
              {starting
                ? progress?.status === "submitted" ? "Finishing..." : "Opening..."
                : participantActionLabel(progress)}
            </ApplicationButton>
          ) : (
            <nav className="av-assignment-complete__actions" aria-label="Completed assignment navigation">
              <Link to="/app/assigned-work">
                View Assigned Work
              </Link>
              <Link to="/app">
                Back to Dashboard
              </Link>
            </nav>
          )}
        </ApplicationSurface>
      )}
    </ApplicationPage>
  );
}
